// src/components/Nexi.js
// ═══════════════════════════════════════════════════════════════════════════
// NEXI — l'assistente della guida, sempre a portata di clic (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Un pallino in basso a destra, accanto al «+» delle azioni rapide: aprendolo compare la
// stessa ricerca di «Come posso aiutarti?» della Guida (components/AssistenteGuida.js).
// Scelte (decise con Paolo):
//  • si presenta per quello che è — l'assistente della GUIDA, non una persona né
//    un'intelligenza artificiale: cerca nelle schede del Centro assistenza, gratis, nel
//    browser, senza salvare le domande; se non trova una risposta sicura propone l'assistenza;
//  • domande pronte adatte alla pagina in cui ci si trova, così si capisce cosa chiedere;
//  • il fumetto «Sono Nexi, come posso aiutarti?» compare UNA volta sola (per browser),
//    poi resta solo il pallino: niente finestre che si aprono da sole;
//  • sta sulla stessa riga del «+» (non sopra), così non copre altro contenuto in più; tutti e
//    due un po' più in alto dell'angolo, dove Netlify può mostrare «Powered by Netlify».
// Lo stesso Nexi c'è sul sito (vetrina/nexi.js), con le stesse risposte.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useRef, useState } from "react";
import { X, MessageSquare } from "lucide-react";
import AssistenteGuida from "./AssistenteGuida";
import { useSezioniApribili } from "./GuidaNexiora";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { apriRichiestaFormazione, avvioDisponibile } from "./RichiestaFormazione";
import { FORMAZIONE } from "../utils/pianiNexiora";

// W426 — UN solo suggerimento, deciso con Paolo: a titolare e amministratori, tra il 3° e il
// 10° giorno di prova, Nexi propone l'avvio gratuito (un aiuto, non una vendita). Si vede una
// volta per persona (users.nexiFormazioneVista, anche cambiando dispositivo); «Non ora» o
// «Richiedi l'avvio» lo chiudono per sempre. Mai agli altri ruoli, mai a prova finita.
const CHIAVE_FORMAZIONE = "nexi_formazione_vista";
export function suggerisciFormazione({ isOrgAdmin, isPlatformOwner, userProfile, org, adesso = new Date(), vistoQui = false }) {
  if (!isOrgAdmin || isPlatformOwner || userProfile?.isDemoVisitor || userProfile?.nexiFormazioneVista || vistoQui) return false;
  if (!avvioDisponibile(org, adesso)) return false;
  const nata = org?.createdAt?.toDate ? org.createdAt.toDate() : (org?.createdAt ? new Date(org.createdAt) : null);
  if (!nata) return false;
  const giorni = (adesso - nata) / 86400000;
  return giorni >= 3 && giorni <= 10;
}

const CHIAVE_FUMETTO = "nexi_fumetto_visto";

// Domande pronte per pagina (verificate: ognuna trova la scheda giusta)
const PER_PAGINA = {
  project: ["Come faccio firmare il rapportino al cliente?", "Come chiedo di fatturare?", "Come registro un DDT?"],
  presenze: ["Come timbro dal telefono?", "Chi approva il cartellino?"],
  "richiesta-ferie": ["Come chiedo un permesso di due ore?", "Il permesso approvato non è nel cartellino"],
  "approvazione-ferie-permessi": ["Il permesso approvato non è nel cartellino", "Come chiedo un permesso di due ore?"],
  "approvazione-cartellini": ["Chi approva il cartellino?", "Come timbro dal telefono?"],
  "nota-spese": ["Come invio la nota spese?", "Chi approva la nota spese?"],
  "approvazione-note-spese": ["Chi approva la nota spese?", "Come invio la nota spese?"],
  "registro-rapportini": ["Come faccio firmare il rapportino al cliente?", "Come invio il rapportino al cliente?"],
  "registro-interventi": ["Come faccio firmare il rapportino al cliente?", "Come invio il rapportino al cliente?"],
  "da-fatturare": ["Come chiedo di fatturare?", "Come preparo un'offerta?"],
  "ordini-materiali": ["Come chiedo materiale al magazzino?", "Come faccio un ordine al fornitore?"],
  "richiedi-nf": ["Come faccio un ordine al fornitore?", "Come chiedo materiale al magazzino?"],
  "centro-acquisti": ["Come faccio un ordine al fornitore?", "Come chiedo materiale al magazzino?"],
  magazzino: ["Come chiedo materiale al magazzino?", "Come faccio un ordine al fornitore?"],
  scadenziario: ["Come aggiungo una scadenza?", "Come ricevo il promemoria via email?"],
  "cruscotto-scadenze": ["Come aggiungo una scadenza?", "Come ricevo il promemoria via email?"],
  users: ["Come invito un collega?", "Come cambio i permessi di un ruolo?"],
  abilitazioni: ["Come cambio i permessi di un ruolo?", "Come invito un collega?"],
  "impostazioni-org": ["Dove imposto il costo orario?", "Come invito un collega?"],
  dipendenti: ["Dove imposto il costo orario?", "Come invito un collega?"],
  "nexiora-insight": ["Il margine è vuoto", "Da dove arrivano i costi?"],
  "registro-commesse": ["Il margine è vuoto", "Come creo una commessa?"],
  "registro-note": ["Come scrivo una nota generale?", "Come sposto una nota?"],
  programmazione: ["Come coloro le celle della programmazione?", "Come prenoto un mezzo?"],
  "parco-mezzi": ["Come prenoto un mezzo?", "Come registro un noleggio?"],
  "registro-noleggi": ["Come registro un noleggio?", "Come prenoto un mezzo?"],
  "segnalazioni-cliente": ["Come chiudo una segnalazione del cliente?", "Come registro una segnalazione del cliente?"],
  "registro-segnalazioni": ["Come chiudo una segnalazione del cliente?", "Come registro una segnalazione del cliente?"],
  "ditte-esterne": ["Quanto lavoro do alle ditte esterne?", "Come aggiungo una ditta esterna?"],
  "sopralluoghi-senza-commessa": ["Come faccio un sopralluogo senza commessa?", "Come collego un sopralluogo alla commessa?"],
  "elaborazione-offerte": ["Come preparo un'offerta?", "Come creo una commessa?"],
};
const GENERALI = ["Come creo una commessa?", "Come segno le ore sulle commesse?", "Come chiedo materiale al magazzino?"];

const leggi = () => { try { return localStorage.getItem(CHIAVE_FUMETTO) === "1"; } catch { return true; } };
const segna = () => { try { localStorage.setItem(CHIAVE_FUMETTO, "1"); } catch { /* browser senza memoria: pazienza */ } };

export function LogoNexi({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" aria-hidden="true">
      <rect width="72" height="72" rx="16" fill="#6d28d9" />
      <path d="M22 50V22h6l16 20V22h6v28h-6L28 30v20z" fill="#fff" />
    </svg>
  );
}

export default function Nexi({ pagina, onNavigate, nascosto = false }) {
  const [aperto, setAperto] = useState(false);
  const [fumetto, setFumetto] = useState(false);
  const [chiave, setChiave] = useState(0); // a ogni apertura la domanda riparte vuota
  const box = useRef(null);
  const { puoAprire } = useSezioniApribili();
  const { currentUser, userProfile, isOrgAdmin, isPlatformOwner } = useAuth();
  const { orgVisitata: org, effectiveOrgId: orgId } = useOrg();
  const [formazione, setFormazione] = useState(false);
  const vistoQui = (() => { try { return localStorage.getItem(CHIAVE_FORMAZIONE) === "1"; } catch { return false; } })();
  const proponi = suggerisciFormazione({ isOrgAdmin, isPlatformOwner, userProfile, org, vistoQui });
  // dopo il fumetto di benvenuto (mai insieme), qualche secondo dopo l'apertura
  useEffect(() => {
    if (!proponi || !leggi()) return undefined;
    // W427: niente suggerimento se un collega ha già prenotato o chiesto l'avvio dell'azienda
    let vivo = true, t = null;
    const avvia = () => { if (vivo) t = setTimeout(() => vivo && setFormazione(true), 6000); };
    if (orgId) getDoc(doc(db, "formazioneAvvii", orgId)).then(s => { if (!s.exists()) avvia(); }).catch(avvia);
    else avvia();
    return () => { vivo = false; if (t) clearTimeout(t); };
  }, [proponi, orgId]);
  const chiudiFormazione = (richiedi) => {
    setFormazione(false);
    try { localStorage.setItem(CHIAVE_FORMAZIONE, "1"); } catch { /* */ }
    if (currentUser?.uid) updateDoc(doc(db, "users", currentUser.uid), { nexiFormazioneVista: true }).catch(() => {});
    if (richiedi) apriRichiestaFormazione("avvio");
  };

  // Il fumetto di benvenuto: una volta sola, dopo qualche secondo, poi sparisce da solo
  useEffect(() => {
    if (leggi()) return undefined;
    const a = setTimeout(() => setFumetto(true), 2500);
    const b = setTimeout(() => { setFumetto(false); segna(); }, 16000);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, []);

  // Esc e clic fuori chiudono la finestra
  useEffect(() => {
    if (!aperto) return undefined;
    const tasto = e => { if (e.key === "Escape") setAperto(false); };
    const fuori = e => { if (box.current && !box.current.contains(e.target)) setAperto(false); };
    window.addEventListener("keydown", tasto);
    document.addEventListener("mousedown", fuori);
    return () => { window.removeEventListener("keydown", tasto); document.removeEventListener("mousedown", fuori); };
  }, [aperto]);

  if (nascosto) return null;

  const apri = () => {
    setFumetto(false); segna();
    if (!aperto) setChiave(k => k + 1);
    setAperto(a => !a);
  };
  const vai = p => { setAperto(false); onNavigate && onNavigate({ page: p }); };

  return (
    <div ref={box} className="nx-flottante" style={{ position: "fixed", right: 88, bottom: 80, zIndex: 6990, fontFamily: "'Inter',sans-serif" }}>
      {fumetto && !aperto && (
        <div role="status" style={{ position: "absolute", right: 0, bottom: 56, width: 230, background: "#1e1b4b", color: "#fff", borderRadius: 12,
          padding: "10px 30px 10px 12px", fontSize: 13, lineHeight: 1.45, boxShadow: "0 8px 30px rgba(0,0,0,.25)", cursor: "pointer" }} onClick={apri}>
          <b>Ciao, sono Nexi!</b> Come posso aiutarti?
          <button onClick={e => { e.stopPropagation(); setFumetto(false); segna(); }} aria-label="Chiudi"
            style={{ position: "absolute", top: 6, right: 6, background: "none", border: "none", color: "rgba(255,255,255,.7)", cursor: "pointer", padding: 2 }}><X size={14} /></button>
          <span style={{ position: "absolute", right: 16, bottom: -6, width: 12, height: 12, background: "#1e1b4b", transform: "rotate(45deg)" }} />
        </div>
      )}

      {formazione && !fumetto && !aperto && (
        <div role="status" aria-label="Suggerimento di Nexi" style={{ position: "absolute", right: 0, bottom: 56, width: 270, background: "#1e1b4b", color: "#fff", borderRadius: 12,
          padding: "12px 14px", fontSize: 13, lineHeight: 1.5, boxShadow: "0 8px 30px rgba(0,0,0,.25)" }}>
          <b>Vuoi impostare Nexiora insieme?</b> Durante la prova c'è un avvio gratuito di {FORMAZIONE.avvioGratuitoMinuti} minuti in video call: dati aziendali, fonte delle ore, permessi e prima commessa.
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button type="button" onClick={() => chiudiFormazione(true)} style={{ background: "#a78bfa", color: "#1e1b4b", border: "none", borderRadius: 8, padding: "6px 10px", fontWeight: 800, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>Richiedi l'avvio</button>
            <button type="button" onClick={() => chiudiFormazione(false)} style={{ background: "transparent", color: "rgba(255,255,255,.8)", border: "1px solid rgba(255,255,255,.3)", borderRadius: 8, padding: "6px 10px", fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>Non ora</button>
          </div>
          <span style={{ position: "absolute", right: 16, bottom: -6, width: 12, height: 12, background: "#1e1b4b", transform: "rotate(45deg)" }} />
        </div>
      )}

      {aperto && (
        <div role="dialog" aria-label="Nexi, l'assistente della guida"
          style={{ position: "absolute", right: -72, bottom: 58, width: "min(380px, calc(100vw - 24px))", maxHeight: "min(580px, calc(100vh - 160px))",
            display: "flex", flexDirection: "column", background: "#fff", borderRadius: 16, boxShadow: "0 18px 50px rgba(30,27,75,.28)", overflow: "hidden", border: "1px solid #ede9fe" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "linear-gradient(135deg,#1e1b4b,#4c1d95)", color: "#fff", flexShrink: 0 }}>
            <LogoNexi size={30} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>Nexi</div>
              <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.7)" }}>L'assistente della guida di Nexiora</div>
            </div>
            <button onClick={() => setAperto(false)} aria-label="Chiudi" style={{ background: "rgba(255,255,255,.12)", border: "none", borderRadius: 8, width: 30, height: 30, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={16} /></button>
          </div>
          <div style={{ padding: "12px 14px 14px", overflowY: "auto" }}>
            <div style={{ background: "#f5f3ff", borderRadius: "4px 12px 12px 12px", padding: "9px 11px", fontSize: 12.5, color: "#3b0764", lineHeight: 1.5, marginBottom: 10 }}>
              Ciao! Scrivimi cosa vuoi fare e ti porto alla spiegazione giusta della guida. Se non la trovo, ti metto in contatto con l'assistenza.
            </div>
            <AssistenteGuida key={chiave} compatto puoAprire={puoAprire} onApri={vai} esempi={PER_PAGINA[pagina] || GENERALI} />
            <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 12, textAlign: "center" }}>Cerco solo nella guida di Nexiora · le domande non vengono salvate</div>
          </div>
        </div>
      )}

      <button onClick={apri} title="Nexi — l'assistente della guida" aria-label="Apri Nexi, l'assistente della guida" aria-expanded={aperto}
        style={{ width: 46, height: 46, borderRadius: "50%", border: "2px solid #fff", background: aperto ? "#4c1d95" : "#1e1b4b", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(30,27,75,.35)", padding: 0 }}>
        {aperto ? <X size={20} color="#fff" /> : <MessageSquare size={20} color="#fff" />}
      </button>
    </div>
  );
}
